'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface ReadinessQuizProps {
  questions: QuizQuestion[];
}

export default function ReadinessQuiz({ questions }: ReadinessQuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-xl p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-3/4"></div>
          <div className="space-y-3">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setShowResults(false);
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question, index) => {
      if (selectedAnswers[index] === question.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  if (showResults) {
    const score = calculateScore();
    const percentage = (score / questions.length) * 100;

    return (
      <div className="bg-card border border-border rounded-xl p-8">
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl font-headline font-bold text-primary">{score}/{questions.length}</span>
          </div>
          <h3 className="font-headline font-semibold text-2xl text-foreground mb-2">
            Your Readiness Score: {percentage.toFixed(0)}%
          </h3>
          <p className="text-muted-foreground font-body">
            {percentage >= 80 ? "You're well-prepared for pet adoption!" :
              percentage >= 60 ? "You're on the right track! Review the areas below." : "Consider learning more before adopting."}
          </p>
        </div>

        <div className="space-y-6 mb-8">
          {questions.map((question, index) => {
            const isCorrect = selectedAnswers[index] === question.correctAnswer;
            return (
              <div key={question.id} className={`p-4 rounded-lg border-2 ${isCorrect ? 'border-success bg-success/5' : 'border-error bg-error/5'
                }`}>
                <div className="flex items-start space-x-3 mb-2">
                  <Icon
                    name={isCorrect ? 'CheckCircleIcon' : 'XCircleIcon'}
                    size={24}
                    variant="solid"
                    className={isCorrect ? 'text-success' : 'text-error'}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-medium text-foreground mb-2">{question.question}</p>
                    <p className="text-sm text-muted-foreground">{question.explanation}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={handleRestart}
          className="w-full px-6 py-3 bg-primary text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300"
        >
          Retake Quiz
        </button>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="bg-card border border-border rounded-xl p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-body text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm font-body font-medium text-primary">{progress.toFixed(0)}% Complete</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <h3 className="font-headline font-semibold text-xl text-foreground mb-6">
        {question.question}
      </h3>

      <div className="space-y-3 mb-8">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswerSelect(index)}
            className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-300 ${selectedAnswers[currentQuestion] === index
                ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/50'
              }`}
          >
            <span className="font-body text-foreground">{option}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
          className="px-6 py-2.5 border-2 border-border text-foreground font-body font-medium rounded-lg hover:bg-muted transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={selectedAnswers[currentQuestion] === undefined}
          className="px-6 py-2.5 bg-primary text-white font-headline font-semibold rounded-lg hover:shadow-elevated hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
        </button>
      </div>
    </div>
  );
}